import { getReasonLabel } from '../utils/bulkIssue.utils'

// ─── BILL_MODE_UNSUPPORTED Constant ─────────────────────────

/**
 * The blocked reason code surfaced when a row has contractInvalid=true.
 * This code is produced both by the backend precheck and by local
 * classification in classifyBulkIssueItems.
 *
 * IMPORTANT: rows with this code MUST NOT be retried with a different lane.
 */
export const BILL_MODE_UNSUPPORTED_CODE = 'BILL_MODE_UNSUPPORTED'

// ─── Local Pre-Classification ────────────────────────────────

/**
 * Classify a list of selected rows into eligible / blocked buckets
 * BEFORE sending them to the backend precheck.
 *
 * Rows with contractInvalid=true are immediately moved to blocked
 * with code=BILL_MODE_UNSUPPORTED and the backend reason/message
 * is preserved if present. No retry with a different lane is allowed
 * for these rows (see ADR-04).
 *
 * All other rows pass through to eligible for the backend precheck.
 *
 * @param {Array<Object>} items - Raw selected row objects
 * @returns {{ eligible: Array<Object>, blocked: Array<Object> }}
 */
export function classifyBulkIssueItems(items = []) {
  const eligible = []
  const blocked = []

  const normalizedItems = Array.isArray(items) ? items : []

  for (const item of normalizedItems) {
    if (!item || typeof item !== 'object') continue

    if (item.contractInvalid) {
      blocked.push({
        ...item,
        code: BILL_MODE_UNSUPPORTED_CODE,
        // Preserve existing backend message if present, otherwise use a default
        message: item.message || 'Loại hóa đơn không được hỗ trợ theo hợp đồng hiện tại',
      })
    } else {
      eligible.push(item)
    }
  }

  return { eligible, blocked }
}

/**
 * Check whether a blocked item carries BILL_MODE_UNSUPPORTED.
 * Used to guard retry flows — rows with this code must never be retried.
 *
 * @param {Object} item - A blocked row object
 * @returns {boolean}
 */
export function isBillModeUnsupported(item) {
  return item?.code === BILL_MODE_UNSUPPORTED_CODE
}

function normalizeBulkIssueSalesInvoiceId(item = {}) {
  const raw = item?.salesInvoiceId ?? item?.id ?? null
  if (raw === null || raw === undefined) return ''
  return String(raw).trim()
}

export function isBulkIssueChecked(checkedEligible = [], salesInvoiceId) {
  const normalizedId = normalizeBulkIssueSalesInvoiceId({ salesInvoiceId })
  return checkedEligible.some((item) => normalizeBulkIssueSalesInvoiceId(item) === normalizedId)
}

export function collectBulkIssueSalesInvoiceIds(items = []) {
  return Array.from(
    new Set(items.map((item) => normalizeBulkIssueSalesInvoiceId(item)).filter(Boolean)),
  )
}

export function sumSelectedEligibleAmount(eligibleItems = [], selectedEligibleIds = []) {
  const selectedIdSet = new Set(
    (Array.isArray(selectedEligibleIds) ? selectedEligibleIds : [])
      .map((id) => normalizeBulkIssueSalesInvoiceId({ salesInvoiceId: id }))
      .filter(Boolean),
  )

  if (!selectedIdSet.size) {
    return 0
  }

  return (Array.isArray(eligibleItems) ? eligibleItems : []).reduce((sum, item) => {
    const salesInvoiceId = normalizeBulkIssueSalesInvoiceId(item)
    if (!selectedIdSet.has(salesInvoiceId)) {
      return sum
    }

    return sum + Number(item?.totalAmount || 0)
  }, 0)
}

export function buildBulkIssueFilterOptions(blockedList = []) {
  const codes = [...new Set(blockedList.map((item) => item?.code).filter(Boolean))]
  return [
    { value: null, text: 'Tất cả lý do' },
    ...codes.map((code) => ({ value: code, text: getReasonLabel(code) })),
  ]
}

export function filterBlockedBulkIssueItems(blockedList = [], filter = null) {
  if (!filter) return blockedList
  return blockedList.filter((item) => item?.code === filter)
}

export function getBulkIssuePrecheckSummary(preCheckResult = null) {
  const summary = preCheckResult?.summary || {}
  const eligible = Array.isArray(preCheckResult?.eligible) ? preCheckResult.eligible : []
  const blocked = Array.isArray(preCheckResult?.blocked) ? preCheckResult.blocked : []

  return {
    total:
      typeof preCheckResult?.total === 'number'
        ? preCheckResult.total
        : eligible.length + blocked.length,
    eligible: typeof summary.eligible === 'number' ? summary.eligible : eligible.length,
    blocked: typeof summary.blocked === 'number' ? summary.blocked : blocked.length,
    eligibleTotalAmount:
      typeof summary.eligibleTotalAmount === 'number'
        ? summary.eligibleTotalAmount
        : eligible.reduce((sum, item) => sum + Number(item?.totalAmount || 0), 0),
  }
}

export function buildBulkIssueOverviewStats({
  totalSelected = 0,
  eligibleCount = 0,
  blockedCount = 0,
} = {}) {
  return [
    { key: 'total', label: 'Đã chọn', value: totalSelected, tone: 'neutral' },
    { key: 'eligible', label: 'Đủ điều kiện', value: eligibleCount, tone: 'success' },
    {
      key: 'blocked',
      label: 'Bị chặn',
      value: blockedCount,
      tone: blockedCount > 0 ? 'danger' : 'muted',
    },
  ]
}

export function buildBulkIssueDecisionSummary({
  selectedEligibleCount = 0,
  blockedCount = 0,
  selectedEligibleAmount = 0,
  formatCurrency = (value) => value,
} = {}) {
  return {
    title: `Sẽ phát hành ${selectedEligibleCount} hóa đơn hợp lệ`,
    amount: `Tổng giá trị ${formatCurrency(selectedEligibleAmount)} ₫`,
    blockedHint:
      blockedCount > 0 ? `${blockedCount} hóa đơn bị chặn, không nằm trong lượt này` : '',
  }
}

export function normalizeBulkIssueInvoiceType(invoiceType) {
  const normalized = typeof invoiceType === 'string' ? invoiceType.trim().toLowerCase() : ''
  if (normalized === 'retail' || normalized === 'wholesale') {
    return normalized
  }

  throw new Error('invoiceType phải là "retail" hoặc "wholesale"')
}

export function createBulkIssueRetryPayload(failed = [], invoiceType) {
  // Per ADR-04: BILL_MODE_UNSUPPORTED rows must never be retried with another lane.
  const retryable = (Array.isArray(failed) ? failed : []).filter(
    (item) => !isBillModeUnsupported(item),
  )

  return {
    invoiceType: normalizeBulkIssueInvoiceType(invoiceType),
    action: 'issue',
    salesInvoiceIds: collectBulkIssueSalesInvoiceIds(retryable),
  }
}
